import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

import LoginPage from "./pages/loginPage/LoginPage";
import CreateAccountPage from "./pages/createAccountPage/CreateAccountPage";
import VerifyEmailPage from "./pages/verifyEmailPage/VerifyEmailPage";
import CreateClientPage from "./pages/createClientPage/CreateClientPage";
import ClientsPage from "./pages/clientsPage/ClientsPage";

import CalendarPage from "./pages/calanderPage/CalendarPage";
import SettingsPage from "./pages/settingsPage/SettingsPage";
import ClientPage from "./pages/clientPage/ClientPage";
import EditClientPage from "./pages/editClientPage/EditClientPage";

import AppLayout from "./components/AppLayout";
import LoadingSpinner from "./components/LoadingSpinner";

import SubscribeModal from "./modals/SubscribeModal";

import { useAuthContext } from "./hooks/useAuthContext";
import { ApiConnectionString } from "./constants/ApiConntection";
import InitialSetUp from "./pages/initialSetUp/InitalSetUp";
import ResetPasswordPage from "./pages/resetPasswordPage/ResetPasswordPage";
import PublicAvailabilityPage from "./pages/publicAvailabilityPage/PublicAvailabilityPage";
import LinksPage from "./pages/linksPage/LinksPage";
import ResetPrivacyPinPage from "./pages/resetPrivacyPinPage/ResetPrivacyPinPage";

function App() {
  const { user, dispatch } = useAuthContext();

  const [profile, setProfile] = useState(null);
  const [subscribeModalDismissed, setSubscribeModalDismissed] = useState(false);

  const refreshPromiseRef = useRef(null);

  const refreshSession = useCallback(async () => {
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = axios
        .post(
          `${ApiConnectionString}/api/refresh`,
          {},
          {
            withCredentials: true,
            _skipAuthRefresh: true,
          },
        )
        .then((response) => {
          dispatch({
            type: "REFRESH",
            payload: response.data,
          });

          return response.data;
        })
        .finally(() => {
          refreshPromiseRef.current = null;
        });
    }

    try {
      return await refreshPromiseRef.current;
    } catch (error) {
      return null;
    }
  }, [dispatch]);

  // Automatically refresh an expired JWT and retry the failed request.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        const authHeader =
          originalRequest?.headers?.Authorization ??
          originalRequest?.headers?.authorization;

        const isAuthenticatedRequest =
          typeof authHeader === "string" && authHeader.startsWith("Bearer ");

        if (
          error.response?.status !== 401 ||
          !originalRequest ||
          originalRequest._skipAuthRefresh ||
          !isAuthenticatedRequest
        ) {
          return Promise.reject(error);
        }

        // Already refreshed once and still got 401 = session is invalid.
        if (originalRequest._retry) {
          dispatch({
            type: "LOGOUT",
            payload: {},
          });

          return Promise.reject(error);
        }

        originalRequest._retry = true;

        const refreshedUser = await refreshSession();

        if (!refreshedUser?.token) {
          dispatch({
            type: "LOGOUT",
            payload: {},
          });

          return Promise.reject(error);
        }

        originalRequest.headers = originalRequest.headers || {};

        originalRequest.headers.Authorization = `Bearer ${refreshedUser.token}`;

        return axios(originalRequest);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [dispatch, refreshSession]);

  const fetchData = async () => {
    if (!user?.token) {
      return;
    }

    try {
      const response = await axios.get(
        `${ApiConnectionString}/api/myProfile/`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        },
      );

      setProfile(response.data);
    } catch (error) {
      console.error("Failed to load profile:", error);
    }
  };

  const handleInitialSetupComplete = async () => {
    setProfile((currentProfile) =>
      currentProfile
        ? {
            ...currentProfile,
            initalSetUp: true,
            initialSetUp: true,
            InitialSetUp: true,
            InitalSetUp: true,
          }
        : currentProfile,
    );

    await fetchData();
  };

  useEffect(() => {
    if (user?.token) {
      fetchData();
    } else {
      setProfile(null);
      setSubscribeModalDismissed(false);
    }
  }, [user]);

  // Check an existing login when the app first loads.
  useEffect(() => {
    const existingUser = JSON.parse(localStorage.getItem("user"));

    const getUser = async () => {
      try {
        await axios.get(`${ApiConnectionString}/api/user/checkstatus/`, {
          headers: {
            Authorization: `Bearer ${existingUser.token}`,
          },

          // Startup refresh is handled manually below.
          _skipAuthRefresh: true,
        });

        dispatch({
          type: "WELCOMEBACK",
          payload: existingUser,
        });
      } catch (error) {
        if (error.response && error.response.status === 401) {
          const refreshedUser = await refreshSession();

          if (!refreshedUser) {
            dispatch({
              type: "LOGOUT",
              payload: {},
            });
          }
        }
      }
    };

    if (existingUser?.token) {
      getUser();
    }
  }, [dispatch, refreshSession]);

  const loadingScreen = (
    <div className="calendar-loading">
      <LoadingSpinner />
    </div>
  );

  const profileIsLoading = user && profile === null;

  const initialSetUpComplete =
    profile?.initialSetUp ??
    profile?.InitialSetUp ??
    profile?.initalSetUp ??
    profile?.InitalSetUp ??
    false;

  const activeSubscription =
    profile?.activeSubscription ?? profile?.ActiveSubscription ?? false;

  const freeTrialExpiresAt =
    profile?.freeTrialExpiresAt ?? profile?.FreeTrialExpiresAt ?? null;

  const freeTrialExpired =
    freeTrialExpiresAt && new Date(freeTrialExpiresAt) < new Date();

  const subscriptionRequired =
    freeTrialExpired === true && activeSubscription === false;

  const shouldShowSubscribeModal =
    user &&
    profile &&
    initialSetUpComplete === true &&
    subscriptionRequired &&
    subscribeModalDismissed === false;

  function ProtectedLayout() {
    if (!user) {
      return <Navigate to="/login" replace />;
    }

    if (profileIsLoading) {
      return loadingScreen;
    }

    if (initialSetUpComplete === false) {
      return (
        <InitialSetUp
          profile={profile}
          onComplete={handleInitialSetupComplete}
        />
      );
    }

    return <AppLayout profile={profile} />;
  }

  return (
    <div className="App">
      <BrowserRouter>
        {shouldShowSubscribeModal && (
          <SubscribeModal
            show={shouldShowSubscribeModal}
            onClose={() => setSubscribeModalDismissed(true)}
          />
        )}

        <Routes>
          <Route
            path="/"
            element={
              !user ? <LoginPage /> : <Navigate to="/appointments" replace />
            }
          />

          <Route
            path="/login"
            element={
              !user ? <LoginPage /> : <Navigate to="/appointments" replace />
            }
          />

          <Route
            path="/availability/:slug"
            element={<PublicAvailabilityPage />}
          />

          <Route
            path="/therapist/resetpassword/:token"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/therapist/resetpin/:token"
            element={<ResetPrivacyPinPage />}
          />

          <Route
            path="/signup"
            element={
              !user ? (
                <CreateAccountPage />
              ) : (
                <Navigate to="/appointments" replace />
              )
            }
          />

          <Route path="/verifyEmailPage/:token" element={<VerifyEmailPage />} />

          <Route element={<ProtectedLayout />}>
            <Route
              path="/appointments"
              element={<CalendarPage profile={profile} />}
            />

            <Route
              path="/Appointments"
              element={<CalendarPage profile={profile} />}
            />

            <Route path="/settings" element={<SettingsPage />} />

            <Route
              path="/clients"
              element={<ClientsPage profile={profile} />}
            />

            <Route path="/client/:id" element={<ClientPage />} />

            <Route path="/client/:id/edit" element={<EditClientPage />} />

            <Route path="/links" element={<LinksPage profile={profile} />} />

            <Route
              path="/clients/create"
              element={
                subscriptionRequired ? (
                  <Navigate to="/clients" replace />
                ) : (
                  <CreateClientPage />
                )
              }
            />
          </Route>

          <Route
            path="*"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : (
                <Navigate to="/appointments" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
